import { createFont } from "tamagui";

import {
  displayFontSize,
  displayLetterSpacing,
  displayLineHeight,
  khTekaFontSize,
  khTekaLineHeight,
} from "../themes/token-fontSizes";

// Loose type for font tokens - allows Tamagui to accept any string/number key
// (including default tokens from @tamagui/config)
type FontTokens = Record<string | number, number>;

// Default font sizes provided by Tamagui, for reference:
// 1: 11,
// 2: 12,
// 3: 13,
// 4: 14,  // Paragraph
// 5: 16,  // H6
// 6: 18,  // H5
// 7: 20,  // H4
// 8: 23,  // H3
// 9: 30,  // H2
// 10: 46, // H1
// 11: 55,
// 12: 62,
// 13: 72,
// 14: 92,
// 15: 114,
// 16: 134,
export const fontSize = {
  1: 11,
  // Body Small
  2: 12,
  // Body 2
  3: 14,
  // Body
  4: 16,
  // Title
  5: 18,
  // Subheadline 5
  6: 20,
  // Subheadline 4
  7: 24,
  // Subheadline 3
  8: 28,
  //  Subheadline 2
  9: 32,
  //  Subheadline 1
  10: 36,
  // Headline 1
  11: 40,
  12: 48,
  13: 60,
  14: 72,
};

// Line height is pulled from the design system.
export const headingLineHeight = 0.8;

/**
 * @deprecated Use degularFont instead
 */
export const headingFont = createFont({
  family: "DegularDisplay",
  size: { ...fontSize, ...displayFontSize, true: 40 } as FontTokens,
  weight: {
    // Standard weight for degular display is Medium
    true: "500",
  },
  lineHeight: {
    ...Object.fromEntries(
      Object.entries(fontSize).map(([k, v]) => [k, v * headingLineHeight]),
    ),
    ...displayLineHeight,
  } as FontTokens,
  // Face has no effect on web - for web mapping, use fonts.css.
  face: {
    normal: {
      normal: "DegularDisplay-Medium",
      italic: "DegularDisplay-MediumItalic",
    },
    bold: {
      normal: "DegularDisplay-Medium",
      italic: "DegularDisplay-MediumItalic",
    },
    // Only Medium weight available
    500: {
      normal: "DegularDisplay-Medium",
      italic: "DegularDisplay-MediumItalic",
    },
  },
});

// Subheadline font is the same as body font, but has a different line height
// and font weights. However, Tamagui does not like multiple fonts with the same
// font family, so we have to modify the font on custom Subheadline components -
// see Typography.tsx for details.
export const subheadlineLineHeight = 1.1;

const bodyLineHeight = 1.45;

/**
 * @deprecated Use khTekaFont instead
 */
export const bodyFont = createFont({
  family: "KHTeka",
  size: { ...fontSize, ...khTekaFontSize, true: 16 } as FontTokens,
  weight: {
    // Standard body typography is Regular.
    1: "400",
    true: "400",
  },
  lineHeight: {
    ...Object.fromEntries(
      Object.entries(fontSize).map(([k, v]) => [k, v * bodyLineHeight]),
    ),
    ...khTekaLineHeight,
  } as FontTokens,
  face: {
    normal: {
      normal: "KHTeka-Regular",
      italic: "KHTeka-RegularItalic",
    },
    bold: {
      normal: "KHTeka-Medium",
      italic: "KHTeka-MediumItalic",
    },
    // Only a subset of weights available.
    300: {
      normal: "KHTeka-Light",
      italic: "KHTeka-LightItalic",
    },
    400: {
      normal: "KHTeka-Regular",
      italic: "KHTeka-RegularItalic",
    },
    500: {
      normal: "KHTeka-Medium",
      italic: "KHTeka-MediumItalic",
    },
    // Fake an in-between weight.
    600: {
      normal: "KHTeka-Bold",
      italic: "KHTeka-BoldItalic",
    },
    700: {
      normal: "KHTeka-Bold",
      italic: "KHTeka-BoldItalic",
    },
    900: {
      normal: "KHTeka-Black",
      italic: "KHTeka-BlackItalic",
    },
  },
});

// =============================================================================
// Typography System Fonts
// These fonts use semantic size tokens (display1, title1, body1, etc.) and are
// designed for the new typography components (Display, Title, Subtitle, Body, etc.)
// =============================================================================

// Degular font for Display1-5 components
// Uses 2% letter spacing and tight line heights
export const degularFont = createFont({
  family: "DegularDisplay",
  size: displayFontSize,
  weight: {
    true: "500",
  },
  lineHeight: displayLineHeight,
  letterSpacing: displayLetterSpacing,
  face: {
    normal: {
      normal: "DegularDisplay-Medium",
      italic: "DegularDisplay-MediumItalic",
    },
    bold: {
      normal: "DegularDisplay-Medium",
      italic: "DegularDisplay-MediumItalic",
    },
    500: {
      normal: "DegularDisplay-Medium",
      italic: "DegularDisplay-MediumItalic",
    },
  },
});

// KHTeka font for Title, Subtitle, Body, and Caption components
export const khTekaFont = createFont({
  family: "KHTeka",
  size: khTekaFontSize,
  weight: {
    true: "400",
    400: "400",
    500: "500",
  },
  lineHeight: khTekaLineHeight,
  face: {
    normal: {
      normal: "KHTeka-Regular",
      italic: "KHTeka-RegularItalic",
    },
    bold: {
      normal: "KHTeka-Medium",
      italic: "KHTeka-MediumItalic",
    },
    400: {
      normal: "KHTeka-Regular",
      italic: "KHTeka-RegularItalic",
    },
    500: {
      normal: "KHTeka-Medium",
      italic: "KHTeka-MediumItalic",
    },
  },
});
